import classes from "./ProfilePermits.module.css";
import { useTranslation } from "react-i18next";
import { Employee, PermitFormBody } from "../../../contexts/interfaces";
import { useRef, useState } from "react";
import { removePermitsByIds } from "../../../scripts/dataHandlers";
import { useUserContext } from "../../../contexts/UserContext";
import { CgCloseO, CgClose, CgBorderStyleSolid } from "react-icons/cg";
import { convertDate } from "../../../utils/convertDate";
import { toast } from "react-toastify";
import { useErrorContext } from "../../../contexts/ErrorContext";
import { useReactToPrint } from "react-to-print";
import ProfilePermitsPrint from "./ProfilePermitsPrint";
import "react-tooltip/dist/react-tooltip.css";

//import ReactTooltip from 'react-tooltip';
//import { FaInfo, FaInfoCircle } from 'react-icons/fa';

interface Props {
  employee: Employee;
  setEmployee: (val: Employee) => void;
}

const ProfilePermits = ({ employee, setEmployee }: Props) => {
  const [removeMode, setRemoveMode] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [confirmRemoveSelected, setConfirmRemoveSelected] =
    useState<boolean>(false);
  const [selectedPermits, setSelectedPermits] = useState<PermitFormBody[]>([]);

  const { user } = useUserContext();

  const { handleError } = useErrorContext();

  const { t } = useTranslation();

  const componentRef = useRef<HTMLDivElement>(null);

  // Toggle state of single checkbox, adding or removing selected permit
  const handleState = (permit: PermitFormBody) => {
    let modifiedArr = selectedPermits;
    // If selected clicked permit is already in selectedPermit array, remove it
    // Else add it to array
    if (!modifiedArr.find((perm) => perm.permitId === permit.permitId)) {
      modifiedArr.push(permit);
    } else {
      modifiedArr.splice(
        modifiedArr.findIndex((p) => p.permitId === permit.permitId),
        1
      );
    }
    setSelectedPermits([...modifiedArr]);
  };

  // Toggle selectAll checkbox and add all / remove all from selected permits
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPermits([]);
    } else {
      setSelectedPermits([...employee.permits]);
    }
    // Toggle opposite value to selectAll
    setSelectAll(!selectAll);
  };

  // Function that calls for request to remove selected permits
  const handleRemoveSelected = async () => {
    if (user) {
      // Send request to remove selected permits from selected nurse
      const permitIds = selectedPermits.map(({ permitId }) => permitId);
      const res = await removePermitsByIds(
        user.id,
        employee.employeeId,
        permitIds
      );

      if (res?.status === 200) {
        // Close everything and set them to back to default
        setConfirmRemoveSelected(false);
        setRemoveMode(false);
        setSelectAll(false);
        setSelectedPermits([]);

        // Update employee in state
        setEmployee({
          ...employee,
          permits: employee.permits.filter(
            (permit) => !permitIds.some((id) => id === permit.permitId)
          ),
        });

        // Show toast message about successful removal
        toast(String(t("message.removePermitSuccess")), {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          type: "success",
        });
      } else {
        handleError(res, "RemovePermitsByIdsFailed");
      }
    }
  };

  // Custome page margins for printing view
  const getPageMargins = () => {
    return `@page { margin: 10px 10px 10px 20px !important; }`;
  };

  // Setup the print functionality with the new API
  const handlePrint = useReactToPrint({
    documentTitle: "Lääkehoitoluvat",
    contentRef: componentRef,
    pageStyle: getPageMargins(),
    suppressErrors: true,
  });

  return (
    <>
      <div className={classes.container}>
        <div className={classes.editRow}>
          <div>
            <div className={classes.title}>{t("profilePermits.mainTitle")}</div>

            {/* PERMIT APPROVING DOCTORS INFO */}
            {/*<div
                            className={classes.infoCircle}
                            data-tooltip-id="doctorTooltip"
                            data-tooltip-content={
                                employee.permits.map((permit) =>
                                    `${permit.doctorName}</br>`).join('')
                            }
                            data-tooltip-html={true}
                        >
                            <FaInfoCircle />
                        </div>
                        <Tooltip id="doctorTooltip" />
                        */}

            {employee.permits.length > 0 && (
              <div className={classes.expiring}>
                {`${t("profilePermits.expiring")}: ${convertDate(
                  employee.permitsValidUntil
                )}` || "N/A"}
              </div>
            )}
          </div>
          <div className={classes.permitButtonContainer}>
            {employee.permits.length > 0 && (
              <>
                <button
                  className={classes.permitButton}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrint();
                  }}
                >
                  {t("profilePermits.printButton")}
                </button>
                <div ref={componentRef}>
                  <ProfilePermitsPrint employee={employee} />
                </div>
                <button
                  className={classes.permitButton}
                  onClick={() => setRemoveMode(!removeMode)}
                >
                  {removeMode
                    ? t("profilePermits.removeButtonTextAbort")
                    : t("profilePermits.removeButtonTextRemove")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* SELECT ALL ELEMENTS */}
        {removeMode && (
          <>
            <div className={classes.selectAllContainer}>
              {selectAll ? (
                <div
                  className={classes.permitCheckboxChecked}
                  onClick={() => handleSelectAll()}
                >
                  <CgBorderStyleSolid />
                </div>
              ) : (
                <div
                  className={classes.permitCheckbox}
                  onClick={() => handleSelectAll()}
                >
                  {""}
                </div>
              )}

              <div className={classes.permitName}>
                {t("profilePermits.selectAll")}
              </div>
            </div>
            <div className={classes.line} />
          </>
        )}

        {/* PERMIT ITEMS */}
        {employee.permits.length > 0 ? (
          <>
            <div className={classes.permitItemContainer}>
              {employee.permits.map((permit, index) => (
                <div key={index} className={classes.permitItem}>
                  {removeMode ? (
                    <div
                      className={
                        selectedPermits.findIndex(
                          (selected) => selected.permitId === permit.permitId
                        ) !== -1
                          ? classes.permitCheckboxChecked
                          : classes.permitCheckbox
                      }
                      onClick={() => handleState(permit)}
                    >
                      {selectedPermits.findIndex(
                        (selected) => selected.permitId === permit.permitId
                      ) !== -1 ? (
                        <CgClose />
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    <div className={classes.permitDot} />
                  )}

                  <div className={classes.permitRow}>
                    <div>{permit.fullTitle + "."}</div>
                    {permit?.doctorName && (
                      <div className={classes.permitDoctorName}>
                        {`${t("profilePermits.approvingDoctor")}: ${
                          permit.doctorName.split("(")[0]
                        }`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              className={classes.removeSelectedButton}
              disabled={
                removeMode && selectedPermits && selectedPermits.length > 0
                  ? false
                  : true
              }
              onClick={() => setConfirmRemoveSelected(true)}
            >
              {t("profilePermits.removeSelectedButton")}
            </button>
          </>
        ) : (
          /* IF EMPLOYEE HAS NO PERMITS */
          <div className={classes.noPermits}>
            {t("profilePermits.noPermits")}
          </div>
        )}

        {/*  CONFIRM MODAL FOR REMOVING SELECTED PERMITS*/}
        {confirmRemoveSelected ? (
          <div className={classes.confirmBackground}>
            <div className={classes.confirmModal}>
              <div className={classes.confirmTitle}>
                {t("profilePermits.confirmTitle")}
              </div>
              <div className={classes.confirmQuestion}>
                {t("profilePermits.confirmQuestion")}
              </div>

              <div className={classes.confirmPermitList}>
                {selectedPermits.map((permit, i) => {
                  return (
                    <div key={i} className={classes.confirmPermitItem}>
                      <div className={classes.confirmItemIcon}>
                        <CgCloseO />
                      </div>
                      <div className={classes.confirmItemName}>
                        {permit.title}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={classes.confirmButtonRow}>
                <button
                  className={classes.cancelButton}
                  onClick={() => setConfirmRemoveSelected(false)}
                >
                  {t("profilePermits.cancel")}
                </button>

                <button
                  className={classes.confirmButton}
                  onClick={() => handleRemoveSelected()}
                >
                  {t("profilePermits.confirm")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default ProfilePermits;
